import { ActivityScheduleConfig } from '../../interface';

export const SCHEDULE_BASE_HOUR = 6;
export const SCHEDULE_HOURS = 23;
export const SCHEDULE_DAY_MINUTES = SCHEDULE_HOURS * 60;
export const UNUSED_SCHEDULE_PLACEMENT: Pick<
    ActivityScheduleConfig,
    'date' | 'used' | 'court' | 'start_at' | 'end_at'
> = {
    date: '',
    used: false,
    court: 0,
    start_at: '',
    end_at: '',
};

export type ScheduleBoardEvent = {
    id: number;
    title: string;
    start: number;
    end: number;
    duration: number;
    courtId: string;
};

export type ScheduleTemplate = {
    id: number;
    title: string;
    duration: number;
    used: boolean;
};

export type CourtEvents = Record<string, ScheduleBoardEvent[]>;

export type AutoAlignResult =
    | { ok: true; placements: ScheduleBoardEvent[] }
    | { ok: false; error: string };

export const cloneScheduleItems = (items: ActivityScheduleConfig[]): ActivityScheduleConfig[] =>
    items.map(item => ({ ...item }));

export const getAvailableScheduleTemplates = <T extends { used: boolean }>(
    templates: readonly T[]
): T[] => templates.filter(template => !template.used);

export const getVisibleScheduleTemplates = <T extends { used: boolean }>(
    templates: readonly T[],
    showAdded: boolean
): T[] => showAdded ? [...templates] : getAvailableScheduleTemplates(templates);

export const updateScheduleItem = (
    items: ActivityScheduleConfig[],
    id: number,
    changes: Partial<ActivityScheduleConfig>
): ActivityScheduleConfig[] => items.map((item, index) =>
    index === id ? { ...item, ...changes } : item
);

export function minutesToScheduleTime(minutes: number): string {
    const totalMinutes = SCHEDULE_BASE_HOUR * 60 + minutes;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const displayHours = hours >= 24 ? hours - 24 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')}`;
}

export function scheduleTimeToMinutes(timeStr: string): number {
    const [hours, mins] = timeStr.split(':').map(Number);
    const adjustedHours = hours < SCHEDULE_BASE_HOUR ? hours + 24 : hours;
    return adjustedHours * 60 + mins - SCHEDULE_BASE_HOUR * 60;
}

export function scheduleDateTimeValue(date: string, time: string): number {
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    const nextDayOffset = hour < SCHEDULE_BASE_HOUR ? 24 * 60 * 60 * 1000 : 0;
    return Date.UTC(year, month - 1, day, hour, minute) + nextDayOffset;
}

export function scheduleDayEndMinutes(validEndTime: number | null): number {
    return validEndTime !== null ? validEndTime : SCHEDULE_DAY_MINUTES;
}

export function isScheduleRangeVisible(
    start: number,
    end: number,
    startTime: number,
    endTime: number,
): boolean {
    return start >= startTime && end <= endTime;
}

export function snapScheduleMinutes(minutes: number, step = 30): number {
    return Math.max(0, Math.round(minutes / step) * step);
}

export function scheduleCourtId(court: number): string {
    return `court${court}`;
}

export function scheduleCourtNumber(courtId: string): number {
    return parseInt(courtId.replace('court', ''), 10) || 0;
}

export function emptyCourtEvents(maxCourtCount: number): CourtEvents {
    const events: CourtEvents = {};
    for (let i = 1; i <= maxCourtCount; i++) {
        events[scheduleCourtId(i)] = [];
    }
    return events;
}

export function scheduleRangesOverlap(
    start: number,
    end: number,
    otherStart: number,
    otherEnd: number,
): boolean {
    return start < otherEnd && end > otherStart;
}

export function courtEventsConflict(
    events: Array<{ id: number; start: number; end: number }>,
    editingEventId: number,
    start: number,
    end: number,
): boolean {
    return events.some(event =>
        event.id !== editingEventId && scheduleRangesOverlap(start, end, event.start, event.end)
    );
}

export const hasCourtScheduleConflict = (
    events: Record<string, Array<{ id: number; start: number; end: number }>>,
    courtId: string,
    editingEventId: number,
    start: number,
    end: number
): boolean => courtEventsConflict(events[courtId] ?? [], editingEventId, start, end);

export const validateScheduleRoundOrder = (items: ActivityScheduleConfig[]): string | null => {
    const windowsByCategory = new Map<number, Map<number, { start: number; end: number; name: string }>>();

    items.forEach(item => {
        if (!item.used || !item.date || !item.start_at || !item.end_at) return;

        const start = scheduleDateTimeValue(item.date, item.start_at);
        const end = scheduleDateTimeValue(item.date, item.end_at);
        const categoryWindows = windowsByCategory.get(item.category_id) ?? new Map();
        const existing = categoryWindows.get(item.round);
        const isEarliestGroup = !existing || start < existing.start;
        categoryWindows.set(item.round, {
            start: existing ? Math.min(existing.start, start) : start,
            end: existing ? Math.max(existing.end, end) : end,
            name: isEarliestGroup ? item.name : existing.name,
        });
        windowsByCategory.set(item.category_id, categoryWindows);
    });

    const categoryWindowsList: Map<number, { start: number; end: number; name: string }>[] = [];
    windowsByCategory.forEach(categoryWindows => categoryWindowsList.push(categoryWindows));
    for (let categoryIndex = 0; categoryIndex < categoryWindowsList.length; categoryIndex++) {
        const categoryWindows = categoryWindowsList[categoryIndex];
        const rounds = Array.from(categoryWindows.keys()).sort((a, b) => a - b);
        for (let index = 1; index < rounds.length; index++) {
            const previousRound = categoryWindows.get(rounds[index - 1])!;
            const currentRound = categoryWindows.get(rounds[index])!;
            if (currentRound.start < previousRound.end) {
                return `${currentRound.name} starts before the previous round finishes`;
            }
        }
    }

    return null;
};

export function clearSchedulePlacement(item: ActivityScheduleConfig): ActivityScheduleConfig {
    return { ...item, ...UNUSED_SCHEDULE_PLACEMENT };
}

export type ClearScheduleScope = {
    categoryId?: number;
    date?: string;
};

export function scheduleItemInClearScope(
    item: Pick<ActivityScheduleConfig, 'category_id' | 'date'>,
    scope: ClearScheduleScope,
): boolean {
    if (scope.categoryId && item.category_id !== scope.categoryId) {
        return false;
    }
    if (scope.date !== undefined && item.date !== scope.date) {
        return false;
    }
    return true;
}

export function clearScheduleItems(
    items: ActivityScheduleConfig[],
    scope: ClearScheduleScope = {},
): ActivityScheduleConfig[] {
    return items.map(item =>
        scheduleItemInClearScope(item, scope) ? clearSchedulePlacement(item) : item
    );
}

export function clearCategorySchedule(
    items: ActivityScheduleConfig[],
    categoryId: number,
): ActivityScheduleConfig[] {
    return clearScheduleItems(items, { categoryId });
}

export function clearScheduleConfirmCopy(
    currentDateOnly: boolean,
    date: string,
    allCategories: boolean,
): { title: string; content: string } {
    if (currentDateOnly) {
        return {
            title: 'Clear this date',
            content: allCategories
                ? `Reset every match scheduled on ${date}?`
                : `Reset this category's matches scheduled on ${date}?`,
        };
    }
    return {
        title: 'Clear all dates',
        content: allCategories
            ? 'Reset every scheduled match on every date?'
            : 'Reset all matches in this category on every date?',
    };
}

export function scheduleItemFromEvent(
    event: Pick<ScheduleBoardEvent, 'courtId' | 'start' | 'end'>,
    date: string,
): Partial<ActivityScheduleConfig> {
    if (!event.courtId) {
        return UNUSED_SCHEDULE_PLACEMENT;
    }
    return {
        used: true,
        court: scheduleCourtNumber(event.courtId),
        start_at: minutesToScheduleTime(event.start),
        end_at: minutesToScheduleTime(event.end),
        date,
    };
}

export function upsertCourtEvent(
    events: CourtEvents,
    event: ScheduleBoardEvent,
): CourtEvents {
    const next: CourtEvents = {};
    Object.keys(events).forEach(courtId => {
        next[courtId] = events[courtId].filter(existing => existing.id !== event.id);
    });
    if (event.courtId) {
        next[event.courtId] = [...(next[event.courtId] ?? []), event];
    }
    return next;
}

export function applySchedulePlacements(
    events: CourtEvents,
    items: ActivityScheduleConfig[],
    placements: ScheduleBoardEvent[],
    date: string,
): { events: CourtEvents; items: ActivityScheduleConfig[] } {
    let nextEvents = events;
    let nextItems = items;
    placements.forEach(placement => {
        nextEvents = upsertCourtEvent(nextEvents, placement);
        nextItems = updateScheduleItem(nextItems, placement.id, scheduleItemFromEvent(placement, date));
    });
    return { events: nextEvents, items: nextItems };
}

export function scheduleTemplatesForCategory(
    items: ActivityScheduleConfig[],
    categoryId = 0,
): ScheduleTemplate[] {
    const sortedItems = [...items].sort((left, right) => {
        if (left.category_id !== right.category_id) {
            return left.category_id - right.category_id;
        }
        return left.round - right.round;
    });

    return sortedItems.flatMap(item => {
        if (categoryId > 0 && item.category_id !== categoryId) {
            return [];
        }
        const originalIndex = items.findIndex(originalItem =>
            originalItem.category_id === item.category_id &&
            originalItem.round === item.round &&
            originalItem.group_key === item.group_key
        );
        return [{
            id: originalIndex,
            title: item.name,
            duration: item.duration,
            used: item.used,
        }];
    });
}

export function courtEventsForDate(
    items: ActivityScheduleConfig[],
    dateStr: string,
    maxCourtCount: number,
): CourtEvents {
    const events = emptyCourtEvents(maxCourtCount);
    items.forEach((item, index) => {
        if (item.date !== dateStr || !item.used) {
            return;
        }
        const courtId = scheduleCourtId(item.court);
        if (!events[courtId]) {
            return;
        }
        events[courtId].push({
            id: index,
            courtId,
            title: item.name,
            duration: item.duration,
            start: scheduleTimeToMinutes(item.start_at),
            end: scheduleTimeToMinutes(item.end_at),
        });
    });
    return events;
}

export function autoAlignSchedule(options: {
    items: ActivityScheduleConfig[];
    matchIds: number[];
    courtIds: string[];
    startMinutes: number;
    endMinutes: number;
    gapMinutes: number;
}): AutoAlignResult {
    const { items, matchIds, courtIds, startMinutes, endMinutes, gapMinutes } = options;
    const selected = matchIds
        .map(id => ({ id, item: items[id] }))
        .filter((row): row is { id: number; item: ActivityScheduleConfig } => Boolean(row.item));

    const matchesByRound = new Map<number, Map<number, typeof selected>>();
    selected.forEach(match => {
        const byGroup = matchesByRound.get(match.item.round) ?? new Map();
        const group = byGroup.get(match.item.group_key) ?? [];
        group.push(match);
        byGroup.set(match.item.group_key, group);
        matchesByRound.set(match.item.round, byGroup);
    });

    const placements: ScheduleBoardEvent[] = [];
    let currentTime = startMinutes;
    const rounds = Array.from(matchesByRound.keys()).sort((left, right) => left - right);

    for (const round of rounds) {
        const groups = Array.from(matchesByRound.get(round)!.entries())
            .sort(([leftKey], [rightKey]) => leftKey - rightKey)
            .map(([groupKey, groupMatches]) => [
                groupKey,
                [...groupMatches].sort((left, right) => left.id - right.id),
            ] as const);
        if (courtIds.length === 1) {
            const soloCourtId = courtIds[0];
            for (const [, groupMatches] of groups) {
                for (const match of groupMatches) {
                    if (currentTime + match.item.duration > endMinutes) {
                        return {
                            ok: false,
                            error: `Auto align failed at round ${round}: Time exceeds valid range for ${match.item.name} in round ${round}`,
                        };
                    }
                    placements.push({
                        id: match.id,
                        title: match.item.name,
                        courtId: soloCourtId,
                        start: currentTime,
                        end: currentTime + match.item.duration,
                        duration: match.item.duration,
                    });
                    currentTime += match.item.duration + gapMinutes;
                }
            }
            continue;
        }

        const courtsPerGroup = Math.ceil(courtIds.length / Math.max(groups.length, 1));
        for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
            const groupMatches = groups[groupIndex][1];
            const groupCourts = courtIds.slice(
                groupIndex * courtsPerGroup,
                groupIndex * courtsPerGroup + courtsPerGroup,
            );
            const effectiveCourts = groupCourts.length > 0 ? groupCourts : courtIds;
            for (let matchIndex = 0; matchIndex < groupMatches.length; matchIndex++) {
                const match = groupMatches[matchIndex];
                if (currentTime + match.item.duration > endMinutes) {
                    return {
                        ok: false,
                        error: `Auto align failed at round ${round}: Time exceeds valid range for ${match.item.name} in round ${round}`,
                    };
                }
                placements.push({
                    id: match.id,
                    title: match.item.name,
                    courtId: effectiveCourts[matchIndex % effectiveCourts.length],
                    start: currentTime,
                    end: currentTime + match.item.duration,
                    duration: match.item.duration,
                });
            }
        }

        const longest = Math.max(
            0,
            ...groups.flatMap(([, groupMatches]) => groupMatches.map(match => match.item.duration)),
        );
        currentTime += longest + gapMinutes;
    }

    return { ok: true, placements };
}
